import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, CalendarDays } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { Calendar, CalendarListView } from '@/components/schedule'
import { CreateEpisodeModal } from '@/components/episodes/CreateEpisodeModal'
import type { Episode, EpisodeStatus } from '@/types'
import { useLabels, singular } from '@/lib/terminology'

interface EpisodeRow {
  id: string
  series_id: string
  number: number
  title: string
  description: string | null
  recording_date: string
  air_date: string
  status: EpisodeStatus
}

function getCurrentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function getToday(): string {
  return new Date().toISOString().split('T')[0]
}

export function SchedulePage() {
  const { membership } = useAuth()
  const supabase = createClient()
  const navigate = useNavigate()
  const labels = useLabels()
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth)
  const [showCreateEpisode, setShowCreateEpisode] = useState(false)

  // Fetch all episodes for the team
  const { data: episodes = [], isLoading } = useQuery({
    queryKey: ['schedule-episodes', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return []

      const { data, error } = await supabase
        .from('episodes')
        .select(`
          id,
          series_id,
          number,
          title,
          description,
          recording_date,
          air_date,
          status
        `)
        .eq('team_id', membership.team_id)

      if (error) throw error

      // Transform to Episode type expected by Calendar
      const rows = (data || []) as EpisodeRow[]
      return rows.map((ep) => ({
        id: ep.id,
        series_id: ep.series_id,
        number: ep.number,
        title: ep.title,
        description: ep.description || undefined,
        guestIds: [],
        guestNames: [],
        recordingDate: ep.recording_date,
        airDate: ep.air_date,
        status: ep.status,
        productionLeadDays: 7,
      })) as Episode[]
    },
    enabled: !!membership?.team_id,
  })

  // Check if team has any series
  const { data: seriesCount = 0 } = useQuery({
    queryKey: ['series-count', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return 0

      const { count, error } = await supabase
        .from('series')
        .select('*', { count: 'exact', head: true })
        .eq('team_id', membership.team_id)

      if (error) throw error
      return count || 0
    },
    enabled: !!membership?.team_id,
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded mb-6" />
          <div className="bg-zinc-100 dark:bg-zinc-800/50 rounded-xl h-96" />
        </div>
      </div>
    )
  }

  const hasEpisodes = episodes.length > 0
  const hasCollections = seriesCount > 0

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{labels.schedule}</h1>
        </div>
        {hasCollections && (
          <button
            onClick={() => setShowCreateEpisode(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Episode
          </button>
        )}
      </div>

      {!hasEpisodes ? (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-12 text-center">
          <CalendarDays className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-2">No episodes scheduled yet</p>
          {hasCollections ? (
            <>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                Create your first episode to see it on the calendar
              </p>
              <button
                onClick={() => setShowCreateEpisode(true)}
                className="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
              >
                Create First Episode
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                Create a {singular(labels.collections).toLowerCase()} first, then add {labels.recordings.toLowerCase()} to see them here
              </p>
              <Link
                to="/collections"
                className="inline-block px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
              >
                Go to {labels.collections}
              </Link>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Desktop: Grid calendar */}
          <div className="hidden md:block">
            <Calendar
              episodes={episodes}
              currentMonth={currentMonth}
              today={getToday()}
              onMonthChange={setCurrentMonth}
              onEventClick={(event) => navigate(`/recordings/${event.episodeId}`)}
            />
          </div>
          {/* Mobile: List view */}
          <div className="md:hidden">
            <CalendarListView
              episodes={episodes}
              currentMonth={currentMonth}
              today={getToday()}
              onMonthChange={setCurrentMonth}
              onEventClick={(event) => navigate(`/recordings/${event.episodeId}`)}
            />
          </div>
        </>
      )}

      {/* Create episode modal */}
      <CreateEpisodeModal
        isOpen={showCreateEpisode}
        onClose={() => setShowCreateEpisode(false)}
      />
    </div>
  )
}
